/**
 * File: UserSideNavigation.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v2.1.0
 * Created: 2025-07-23
 * Last Updated: 2026-06-23
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useState } from "react";

// Local project files
import Logo from "../ui/Logo";
import SidebarItem from "../ui/SidebarItem";
import UserCard from "../ui/UserCard";

/**
 * User side navigation component that renders the sidebar navigation for user routes.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Object} props.schema - Configuration schema used to define sidebar items and behavior
 * @param {boolean} props.isOpen - Indicates whether the sidebar is open
 * @param {Function} props.setIsOpen - Function to update sidebar open state
 *
 * @returns {JSX.Element} Rendered user side navigation
 */
function UserSideNavigation({ schema, isOpen, setIsOpen }) {
    const [isHovering, setIsHovering] = useState(false);
    const [lockHover, setLockHover] = useState(false);

    const isLogoVisible = isOpen || (!isOpen && !isHovering);
    const isPanelButtonVisible = isOpen || (!isOpen && isHovering);

    const handleMouseEnter = () => {
        if (lockHover) return;
        if (!isOpen) setIsHovering(true);
    };

    const handleMouseLeave = () => {
        if (lockHover) return;
        if (!isOpen) setIsHovering(false);
    };

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
        setIsHovering(false);
        setLockHover(true);
        setTimeout(() => setLockHover(false), 80);
    };

    const handleCloseToggle = () => {
        if (!isOpen) handleToggle();
    };

    return (
        <nav
            className={`user-side-navigation ${!isOpen ? "user-side-navigation--close" : ""}`}
        >
            <div
                className={`user-side-navigation__header ${!isOpen ? "user-side-navigation__header--close" : ""}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Logo
                    {...schema.logo}
                    className={
                        isLogoVisible
                            ? "user-side-navigation__logo--visible"
                            : "user-side-navigation__logo--hidden"
                    }
                />
                <div>
                    <SidebarItem
                        key={schema.sidebarItems.header.id}
                        {...schema.sidebarItems.header}
                        onClick={handleToggle}
                        isCollapsed={!isOpen}
                        className={
                            isPanelButtonVisible
                                ? "user-side-navigation__sidebar-item--visible"
                                : "user-side-navigation__sidebar-item--hidden"
                        }
                    />
                </div>
            </div>
            <div
                className={`user-side-navigation__container ${!isOpen ? "user-side-navigation__container--close" : ""}`}
                onClick={handleCloseToggle}
            >
                <div className="user-side-navigation__links">
                    {schema.sidebarItems.home.map((item) => (
                        <SidebarItem
                            key={item.id}
                            {...item}
                            isCollapsed={!isOpen}
                        />
                    ))}
                </div>
                <div className="user-side-navigation__links">
                    {schema.sidebarItems.support.map((item) => (
                        <SidebarItem
                            key={item.id}
                            {...item}
                            isCollapsed={!isOpen}
                        />
                    ))}
                </div>
            </div>
            <div>
                <UserCard {...schema.userCard} isDetails={isOpen} />
            </div>
        </nav>
    );
}

export default UserSideNavigation;
